import { useCallback, useEffect, useState } from "react";
import { TrendUp, TrendDown, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResearchButton } from "@/components/Research";

const tone = (n) => (n == null ? "text-slate-400" : n > 0 ? "text-[#0B5C2E]" : n < 0 ? "text-[#B3241B]" : "text-slate-500");
const band = (n) => {
  if (n == null) return "bg-slate-100 text-slate-500";
  if (n >= 1.5) return "bg-[#0B5C2E] text-white";
  if (n > 0.3) return "bg-[#CDEBD9] text-[#0B5C2E]";
  if (n >= -0.3) return "bg-slate-100 text-slate-600";
  if (n > -1.5) return "bg-[#FADBD8] text-[#8E1C15]";
  return "bg-[#B3241B] text-white";
};
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(2)}%`);

const MoverRow = ({ row, i, prefix }) => (
  <li data-testid={`${prefix}-${i}`} className="flex items-baseline justify-between gap-3 border-b border-border py-2 last:border-0">
    <span className="text-sm text-[#0F172A]">{row.name} <span className="text-xs text-slate-500">{row.country}</span></span>
    <span className={`text-sm font-semibold ${tone(row.change_pct)}`}>{pct(row.change_pct)}</span>
  </li>
);

export const WorldBoard = ({ canRefresh = false, canResearch = false }) => {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("all");
  const [read, setRead] = useState(null);
  const [engine, setEngine] = useState("gpt");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/world/board${region === "all" ? "" : `?region=${encodeURIComponent(region)}`}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ indices: [], unavailable: true, regions: [], movers: { risers: [], fallers: [] } }));
  }, [region]);

  useEffect(load, [load]);
  useEffect(() => {
    const timer = setInterval(load, 300000);
    return () => clearInterval(timer);
  }, [load]);

  const askRead = async (which = engine) => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/world/analysis?engine=${which}`);
      setRead(d);
      setEngine(which);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const force = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/world/refresh", {});
      toast.success(`${d.refreshed} index level(s) pulled`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!data) return <p className="py-12 text-center text-sm text-slate-500">Loading the world board…</p>;

  if (data.unavailable) {
    return (
      <p data-testid="world-unavailable" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
        The world market feed is not reachable right now. It refreshes every 15 minutes — try again shortly.
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="world-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500" data-testid="world-as-of">
          {data.breadth ? `${data.breadth.up} of ${data.breadth.total} markets up` : "No moves yet"}
          {data.as_of ? ` · as of ${new Date(data.as_of).toLocaleString()}` : ""}
          {data.stale ? " · last good snapshot" : ""}
          {data.source ? ` · ${data.source}` : ""}
        </p>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="world-analysis-btn" onClick={() => askRead()} disabled={busy}>
            <Sparkle size={15} className="mr-1.5" /> {busy ? "Reading…" : "Read the day"}
          </Button>
          {canRefresh && (
            <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="world-refresh-btn" onClick={force} disabled={busy}>
              <ArrowsClockwise size={15} className="mr-1.5" /> Refresh
            </Button>
          )}
        </div>
      </div>

      {read && (
        <div data-testid="world-analysis" className="border-l-2 border-[#B3241B] bg-[#FFF6E5] px-4 py-3">
          <p className="text-sm leading-relaxed text-[#0F172A]">{read.summary}</p>
          {read.impact?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {read.impact.map((line, i) => <li key={i} data-testid={`world-impact-${i}`}>· {line}</li>)}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#F0DEBC] pt-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Written by</span>
            {[["gpt", "GPT-5.5"], ["claude", "Claude Sonnet 4.6"]].map(([key, label]) => (
              <button key={key} data-testid={`world-engine-${key}`} disabled={busy}
                onClick={() => askRead(key)}
                className={`tap-safe border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 disabled:opacity-60 ${
                  (read.engine || engine) === key
                    ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
            <span className="text-[11px] text-slate-400" data-testid="world-engine-note">
              {read.cached ? "from cache" : "fresh"}
            </span>
          </div>
        </div>
      )}

      {canResearch && (
        <ResearchButton
          label="Research what is moving the markets"
          endpoint="/research/ask"
          body={{ question: "What is moving world share markets, freight rates and industrial commodity prices right now?", scope: "market" }}
          testid="world-research-btn"
          className="w-full justify-center sm:w-auto"
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="world-regions">
        {data.regions.map((r, i) => (
          <div key={r.region} data-testid={`world-region-${i}`} className={`p-4 ${band(r.avg_change_pct)}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{r.region}</p>
            <p className="mt-2 text-2xl font-bold">{pct(r.avg_change_pct)}</p>
            <p className="mt-1 text-xs opacity-80">{r.up} up · {r.down} down</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-white p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><TrendUp size={16} className="text-[#0B5C2E]" /> Biggest risers</p>
          <ul className="mt-2" data-testid="world-risers">
            {data.movers.risers.map((row, i) => <MoverRow key={row.symbol} row={row} i={i} prefix="world-riser" />)}
          </ul>
        </div>
        <div className="border border-border bg-white p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><TrendDown size={16} className="text-[#B3241B]" /> Biggest fallers</p>
          <ul className="mt-2" data-testid="world-fallers">
            {data.movers.fallers.map((row, i) => <MoverRow key={row.symbol} row={row} i={i} prefix="world-faller" />)}
          </ul>
        </div>
      </div>

      <div className="flex gap-2 scroll-x overflow-x-auto pb-1" data-testid="world-region-filter">
        {[["all", "Every region"], ...(data.region_options || []).map((r) => [r, r])].map(([key, label]) => (
          <button key={key} data-testid={`world-region-tab-${key}`} onClick={() => setRegion(key)}
            className={`tap-safe whitespace-nowrap border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
              region === key ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-border bg-white text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="world-index-cards">
        {data.indices.map((row, i) => (
          <div key={row.symbol} data-testid={`world-index-${i}`} className="border border-border bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-[#0F172A]">{row.name}</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                  {row.country} · {row.region} · {row.currency}
                </p>
              </div>
              <span className={`text-sm font-semibold ${tone(row.change_pct)}`} data-testid={`world-change-${i}`}>{pct(row.change_pct)}</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-[#0F172A]" data-testid={`world-value-${i}`}>
              {row.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
            <p className="mt-2 text-[11px] text-slate-400">
              {row.traded_at ? `Traded ${new Date(row.traded_at).toLocaleString()}` : "—"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
